import type { DataSetsProps } from '@/types';
import { getOnStorage, removeFromStorage, setOnStorage } from './storageFunctions';

const IMAGE_DATASET_TYPES = new Set(['imagens', 'imagens_zip']);

export const isImageDataset = (dataset: Pick<DataSetsProps, 'TIPO_ARQUIVO'> | string): boolean => {
  const tipo = typeof dataset === 'string' ? dataset : dataset?.TIPO_ARQUIVO;
  return IMAGE_DATASET_TYPES.has(String(tipo || ''));
};

export const getKnownDatasetUrls = (datasets: DataSetsProps[] | null | undefined): Set<string> =>
  new Set((datasets || []).map((item) => item.URL_S3).filter(Boolean));

export const hasImagesInDbPayload = (imagesPayload: unknown): boolean => {
  if (!imagesPayload) return false;
  if (Array.isArray(imagesPayload)) return imagesPayload.length > 0;
  if (typeof imagesPayload === 'object') return Object.keys(imagesPayload as object).length > 0;
  return false;
};

export const readImageDatasets = (): DataSetsProps[] => getOnStorage('datasets_imagens') || [];

export const writeImageDatasets = (datasets: DataSetsProps[]) => {
  setOnStorage('datasets_imagens', datasets);
};

export const clearImageDatasets = () => {
  removeFromStorage('datasets_imagens');
};

export const migrateImageDatasetsFromMain = (): DataSetsProps[] => {
  const mainDatasets = getOnStorage('datasets') || [];
  const mainWithoutImages = mainDatasets.filter((dataset) => !isImageDataset(dataset));
  const imagesFromMain = mainDatasets.filter((dataset) => isImageDataset(dataset));
  const currentImages = readImageDatasets();

  if (imagesFromMain.length) {
    const byUrl = new Map<string, DataSetsProps>();
    [...currentImages, ...imagesFromMain].forEach((dataset) => {
      if (!dataset?.URL_S3) return;
      byUrl.set(dataset.URL_S3, dataset);
    });
    writeImageDatasets(Array.from(byUrl.values()));
    setOnStorage('datasets', mainWithoutImages);
    return Array.from(byUrl.values());
  }

  return currentImages;
};

export const mergeDatasetsByUrl = (
  current: DataSetsProps[],
  incoming: DataSetsProps[]
): DataSetsProps[] => {
  const byUrl = new Map<string, DataSetsProps>();
  [...current, ...incoming].forEach((dataset) => {
    if (!dataset?.URL_S3) return;
    byUrl.set(dataset.URL_S3, dataset);
  });
  return Array.from(byUrl.values());
};

export const resolveImageDatasetUrlsForSync = ({
  storedImageDatasets,
  apiImageDatasets,
  imagesInDb,
}: {
  storedImageDatasets: DataSetsProps[];
  apiImageDatasets: DataSetsProps[];
  imagesInDb: unknown;
}): { imageUrlSet: Set<string>; syncedImageDatasets: DataSetsProps[] } => {
  const hasImagesInDb = hasImagesInDbPayload(imagesInDb);

  if (!hasImagesInDb) {
    clearImageDatasets();
    return { imageUrlSet: new Set(), syncedImageDatasets: [] };
  }

  if (storedImageDatasets.length) {
    return {
      imageUrlSet: getKnownDatasetUrls(storedImageDatasets),
      syncedImageDatasets: storedImageDatasets,
    };
  }

  if (apiImageDatasets.length) {
    writeImageDatasets(apiImageDatasets);
    return {
      imageUrlSet: getKnownDatasetUrls(apiImageDatasets),
      syncedImageDatasets: apiImageDatasets,
    };
  }

  return { imageUrlSet: new Set(), syncedImageDatasets: [] };
};
